import type { VueConstructor } from 'vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { DatePicker } from '@cognitoforms/element-ui';
import type { DatePickerType } from '@cognitoforms/element-ui/types/date-picker';
import { isComponent } from 'src/util/dom';

type DatePickerInternals = {
	userInput: string;
	parsedValue: Date;
	readonly picker: any;
	isValidValue(value: any): boolean;
	pickerVisible: boolean;
	blur(): void;
	readonly ranged: boolean;
	readonly refInput: HTMLInputElement[];
};

const ElementDatePickerConstructor = Vue.extend(DatePicker) as VueConstructor<DatePicker & DatePickerInternals>;

const HAVE_TRIGGER_TYPES = [
	'date',
	'datetime',
	'time',
	'time-select',
	'week',
	'month',
	'year',
	'daterange',
	'monthrange',
	'timerange',
	'datetimerange',
	'dates'
];

@Component({})
export default class CDatePicker extends ElementDatePickerConstructor {
	// -------------- Properties --------------
	@Prop({ default: null })
	modelFormat: string;

	@Prop({ required: true })
	formatDate: (value: Date, format: string) => string;

	@Prop({ required: true })
	parseDate: (value: string, format: string) => Date;

	@Prop({ default: null })
	unparsedValue: string;

	// -------------- Data --------------
	isFocusing: boolean = false;
	willRaiseBlur: boolean = false;
	willRestoreFocus: boolean = false;
	willRestoreFocusTimestamp: number = 0;
	type: DatePickerType = 'date';
	internalValue: Date = null;
	internalUnparsedValue: string = null;
	setSoftCloseOnPick: boolean = false;

	// -------------- LifeCycle Hooks --------------
	created() {
		this.internalValue = this.value as Date;
		this.internalUnparsedValue = this.unparsedValue;
	}

	mounted() {
		// Raise an event when the input is blurred
		const input = this.$refs.reference;
		if (input && isComponent(input)) {
			// TODO: Is this kosher, if not we could update the date-picker to subscribe to the input's events in the template?
			input.$on('blur', (evt: FocusEvent) => this.handleInputBlur(evt));
		}
	}

	// -------------- Computed --------------
	get displayValue(): string {
		if (this.userInput) {
			return this.userInput;
		}
		else if (this.internalUnparsedValue !== null) {
			return this.internalUnparsedValue;
		}
		else if (this.internalValue != null) {
			return this.formatDate(this.internalValue, this.modelFormat);
		}
		else if (this.parsedValue != null) {
			return this.formatDate(this.parsedValue, this.modelFormat);
		}
		else {
			return '';
		}
	}

	// -------------- Watchers --------------
	@Watch('value', { immediate: true })
	onValueChanged(value: Date | string | string[] | Date[]) {
		this.internalValue = value as Date;
	}

	@Watch('unparsedValue', { immediate: true })
	onUnparsedValueChanged(unparsedValue: string) {
		this.internalUnparsedValue = unparsedValue;
	}

	togglePickerVisible(isVisible: boolean) {
		if (this.$el) {
			const bounds = this.$el.getBoundingClientRect();
			this.$emit('pickerPosition', { top: Math.ceil(bounds.top), bottom: Math.ceil(bounds.bottom) });
		}
		this.pickerVisible = isVisible;
	}

	// -------------- Methods --------------
	parseString(value: string): Date {
		return this.parseDate(value, this.modelFormat) as Date;
	}

	formatToString(value: Date): string {
		return this.formatDate(value, this.modelFormat);
	}

	handleInputBlur(evt: FocusEvent) {
		if (!this.pickerVisible && !this.willRaiseBlur && !this.willRestoreFocus) {
			// Handle and emit change since the change event won't be raised by either the `pickerVisible` watcher or explicit blur logic
			this.handleChange();
			this.emitChange(this.value as string | Date);
			// Raise the blur event here, since the blur event won't be raised due to hiding the picker or explicitly blurring the input
			this.$emit('blur', evt);
		}
	}

	handleFocus(event: FocusEvent | MouseEvent): void {
		const type = this.type;

		const isClickToFocus = event && event.type === 'click';

		if (HAVE_TRIGGER_TYPES.indexOf(type) !== -1 && isClickToFocus && !this.pickerVisible && !this.isFocusing) {
			this.togglePickerVisible(true);
			// After the date is selected, make sure to keep the focus on the date's input field
			if (!this.setSoftCloseOnPick) {
				setTimeout(() => {
					this.picker.$on('pick', (value, keepOpen) => {
						if (!keepOpen)
							this.softClose();
					});
					this.setSoftCloseOnPick = true;
				});
			}
		}

		this.isFocusing = false;

		this.$emit('focus', this);
	}

	handleKeydown(event: KeyboardEvent): void {
		const keyCode = event.keyCode;

		// ALT + UP
		if (event.altKey && event.keyCode === 38) {
			// Handle change before hiding the picker in case the user is typing text already
			this.handleChange();
			// NOTE: Alt+Up behaves the same way as escape in terms of hiding the picker
			// Note that the picker intends to keep focus even though hiding the picker will cause it to temporarily lose focus
			this.softClose(event);
			return;
		}

		// ALT + DOWN
		if (event.altKey && event.keyCode === 40) {
			// Handle change before showing the picker in case the user typed text while the picker was closed
			this.handleChange();
			// Show the picker
			this.togglePickerVisible(true);
			event.stopPropagation();
			return;
		}

		// ESC
		if (keyCode === 27) {
			this.softClose(event);
			return;
		}

		// Tab
		if (keyCode === 9) {
			// requestAnimationFrame is needed in FireFox and Edge to allow focus to be set on next element instead of body
			requestAnimationFrame(() => {
				if (!this.ranged) {
					this.handleChange();

					// Note that the picker is explicitly blurring its input and will raise an emit event either here, or in the `pickerVisible` watcher
					this.willRaiseBlur = true;
					if (!this.pickerVisible && !(this.picker && this.picker.visible)) {
						// NOTE: Copied from 'pickerVisible' watcher
						this.emitChange(this.value as string | Date);
						this.userInput = null;
						this.$emit('blur', this);
					}
					else {
						if (this.picker) {
							this.picker.visible = false;
						}
						this.togglePickerVisible(false);
					}
					this.blur();
					this.willRaiseBlur = false;
				}
				else {
					// user may change focus between two input
					if (this.refInput.indexOf(document.activeElement as any) === -1) {
						// Note that the picker is explicitly blurring its input and will raise the blur event (via the `pickerVisible` watcher)
						this.willRaiseBlur = true;
						this.togglePickerVisible(false);
						this.blur();
						this.willRaiseBlur = false;
					}
				}
			});
			return;
		}

		// Enter
		if (keyCode === 13) {
			event.preventDefault();

			if (!this.pickerVisible) {
				// Handle change before showing the picker in case the user typed text while the picker was closed
				this.handleChange();
				// Show the picker, i.e. what is done when the input is focused
				this.togglePickerVisible(true);
				event.stopPropagation();
				return;
			}
			else {
				// Handle change before hiding the picker in case the user is typing text already
				this.handleChange();
				this.softClose(event);
			}

			if (this.userInput === '' || this.isValidValue(this.parseString(this.displayValue))) {
				this.handleChange();
				if (this.picker) {
					this.picker.visible = false;
				}
				this.togglePickerVisible(false);
				this.blur();
			}
			this.softClose(event);
			return;
		}

		// if user is typing, do not let picker handle key input
		if (this.userInput) {
			event.stopPropagation();
			return;
		}

		// delegate other keys to panel
		if (this.picker && this.picker.handleKeydown) {
			this.picker.handleKeydown(event);
		}
	}

	emitInput(val: string | Date): void {
		const value = typeof val === 'string' ? this.parseDate(val, this.modelFormat) : val || null;
		let unparsedValue = this.internalUnparsedValue;

		if (value) {
			this.internalValue = value;
			this.internalUnparsedValue = null;
		}
		else {
			this.internalValue = null;
			this.internalUnparsedValue = unparsedValue = typeof val === 'string' ? val : null;
		}

		if (value !== this.value || unparsedValue !== this.unparsedValue) {
			this.$emit('input', value, unparsedValue);
		}

		if (this.picker && value && typeof val === 'string') {
			// Update the picker's selected value when a valid date is typed
			this.picker.value = value;
		}
	}

	emitChange(val: string | Date): void {
		if (this.willRestoreFocus && this.willRestoreFocusTimestamp && (Date.now() - this.willRestoreFocusTimestamp) < 100) {
			// Don't raise a change event if we're going to immediately restore focus to the input,
			// since conceptually the input is maintaining focus and so this shouldn't be a change
			return;
		}

		// NOTE: The element picker calls `emitChange(null)` in most cases, the only exception is `emitChange(this.value)` when a
		// date is picked from the date picker. In that case, the 'input' event has been raised with the selected value, but since
		// there's no guarantee that the emitted input has been pushed back down to the components `value` prop, then use the internal
		// value that we're tracking instead. If the `value` prop is re-bound, then `internalValue` will get set to that value anyway.
		val = this.internalValue;

		const value = typeof val === 'string' ? this.parseDate(val, this.modelFormat) : val || null;
		const unparsedValue = this.internalUnparsedValue;
		if (value !== this.value || unparsedValue !== this.unparsedValue) {
			this.$emit('change', value, unparsedValue);
		}
	}

	handleInput(value: string): void {
		this.userInput = value;
		this.emitInput(value);
	}

	handleChange(): void {
		if (this.userInput) {
			const displayValue = this.displayValue;
			const value = this.parseString(displayValue);
			if (value) {
				this.internalUnparsedValue = null;
				if (this.picker) {
					this.picker.value = value;
				}
				if (this.isValidValue(value)) {
					this.emitInput(displayValue);
					this.userInput = null;
				}
			}
			else {
				this.internalUnparsedValue = this.userInput;
				this.emitInput(this.userInput);
			}
		}
		else {
			this.internalUnparsedValue = null;
		}

		if (this.userInput === '') {
			this.emitInput(null);
			this.emitChange(null);
			this.userInput = null;
		}
	}

	// Closes the date picker while keeping focus on the date's input field
	softClose(event: Event | void) {
		// Note that the picker intends to keep focus even though hiding the picker will cause it to temporarily lose focus
		this.willRestoreFocus = true;
		this.willRestoreFocusTimestamp = Date.now();
		// Make note of the current user input so that it can be restored after closing the picker
		const userInputToRestore = this.userInput;
		// Hide the picker: this will raise change, clear out user input, and blur the input
		this.togglePickerVisible(false);
		if (event)
			event.stopPropagation();
		// Restore focus to the input with the previously typed value, if any
		const input = this.refInput[0];
		if (input) {
			setTimeout(() => {
				this.willRestoreFocus = false;
				this.isFocusing = true;

				try {
					// Restore the user input, since it would have been cleared out by the `pickerVisible` watcher
					if (userInputToRestore)
						this.userInput = userInputToRestore;
					input.focus();
				}
				catch (e) { }

				this.isFocusing = false;
			}, 0);
		}
		else {
			this.willRestoreFocus = false;
		}
	}
}
